<template>
  <div>
    <!-- Start: Update Request Dialog -->
    <v-dialog
        v-model="updateRequestDialog"
        max-width="750px"
        persistent
    >
      <v-card>
        <v-card-title>
          Request for Card Update
        </v-card-title>
        <v-card-subtitle>
          {{ selectedNotification.userName }} is requesting an update of the following information on their Business
          Card.
        </v-card-subtitle>
        <v-card-text class="my-0 py-0">
          <v-container>
            <v-form
                ref="form"
                v-model="valid"
            >
              <v-card class="px-5 pt-5 mb-5" flat v-for="employerUpdateRequest in employerUpdateRequests"
                      :key="employerUpdateRequest.employerUpdateRequestId">
                <v-row>
                  <v-col cols="12" md="7" class="my-0 py-0"
                         v-if="['firstName', 'lastName', 'jobTitle', 'email'].includes(employerUpdateRequest.fieldName)">
                    <v-row>
                      <v-text-field
                          dense
                          outlined
                          :rules="employerUpdateRequest.fieldName == 'email'? emailRules : nameRules"
                          :label="`New ${getReadableFieldName(employerUpdateRequest.fieldName)}`"
                          v-model="employerUpdateRequest.newValue"
                          required
                      >
                      </v-text-field>
                    </v-row>
                    <v-row class=" mt-n3">
                      <v-list-item two-line class="pa-0 ml-1">
                        <v-list-item-content>
                          <v-list-item-subtitle>{{ `Old ${getReadableFieldName(employerUpdateRequest.fieldName)}` }}
                          </v-list-item-subtitle>
                          <v-list-item-title class="font-weight-medium">{{ employerUpdateRequest.oldValue }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="7" class="ma-0 pa-0" v-if="employerUpdateRequest.fieldName=='mobile'">
                    <v-row>
                      <v-col cols="4" class="my-0 py-0">
                        <v-autocomplete
                            v-model="selectedTelephoneCode"
                            label="Country"
                            required outlined
                            dense
                            :items="telephoneCodes"
                            item-text="countryCode"
                            item-value="countryCode"
                            return-object
                            :filter="filterTelephoneCodeAutocompleteBox"
                        >
                          <template slot="item" slot-scope="data">
                            {{ data.item.name }} ({{ data.item.countryCode }})
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="8" class="my-0 py-0">
                        <v-text-field
                            :prefix="selectedTelephoneCode.number"
                            label="Mobile"
                            required
                            outlined
                            dense
                            v-model="employerUpdateRequest.newValue"
                            :rules="mobileRules"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row class=" mt-n3 pl-3">
                      <v-list-item two-line class="pa-0 ml-1">
                        <v-list-item-content>
                          <v-list-item-subtitle>Old Mobile Number</v-list-item-subtitle>
                          <v-list-item-title class="font-weight-medium">
                            {{ employerUpdateRequest.countryPhoneCodeNumber }} {{ employerUpdateRequest.oldValue }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="5" class="my-0 py-0" align="center" justify="center">
                    <v-btn depressed large class="warning ml-4 mb-5 primary--text px-5"
                           @click="acceptEmployerUpdateRequest(employerUpdateRequest)">
                      Accept
                    </v-btn>
                    <v-btn depressed large class="info ml-4 mb-5 accent--text px-5"
                           @click="declineEmployerUpdateRequest(employerUpdateRequest)">
                      Decline
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="updateRequestDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End: Update Request Dialog -->
    <!-- Start: Already Actioned Dialog -->
    <v-dialog
        v-model="alreadyActionedDialog"
        max-width="380"
    >
      <v-card>
        <v-card-title class="headline">Great Job!</v-card-title>
        <v-card-text>
          You have already actioned this task
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
              color="primary"
              text
              @click="alreadyActionedDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End: Already Actioned Dialog -->
  </div>
</template>
<script>
import {mapGetters} from 'vuex'

import {getFirestore, increment, where, writeBatch} from "firebase/firestore";
import {
  businessCardsDocRef,
  businessPortalNotificationsDocRef,
  businessPortalStatsDocRef, employerUpdateRequestsDocRef,
  getQueryEmployerUpdateRequestsCollection, updateBusinessCardsDoc, updateEmployerUpdateRequestsDoc
} from "@/shared/firebase_utils";


export default {
  data() {
    return {
      employerUpdateRequests: [],
      selectedTelephoneCode: {countryCode: 'AU', number: '+61'},

      emailRules: [
        v => !!v || 'This field is required',
        v => /.+@.+\..+/.test(v) || 'Invalid email',
      ],
      nameRules: [
        v => !!v || 'This field is required',
      ],
      mobileRules: [
        v => !!v || 'This field is required',
        v => /^\d+$/.test(v) || 'Invalid mobile number'
      ],
      valid: false,
      updateRequestDialog: false,
      alreadyActionedDialog: false,
    };
  },
  props: {
    selectedNotification: {
      type: Object,
      default: {},
    },
    present: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters(['userProfile', 'telephoneCodes']),
    businessPortalStatsDocumentRef() {
      return businessPortalStatsDocRef(this.userProfile.businessProfileId);
    },
  },
  watch: {
    present: function (newVal, oldVal) {
      if (oldVal == false && newVal == true)
        this.handleNotificationClick();
    },
    updateRequestDialog: function (newVal, oldVal) {
      if (newVal == false)
        this.$emit('handled');
    },
    alreadyActionedDialog: function (newVal, oldVal) {
      if (newVal == false)
        this.$emit('handled');
    },
  },

  methods: {
    handleNotificationClick() {
      if (!this.selectedNotification.isRead) {
        //Mark the notification as read
        const db = getFirestore();
        const batch = writeBatch(db);
        batch.update(businessPortalNotificationsDocRef(this.selectedNotification.notificationId), {isRead: true})
        batch.update(this.businessPortalStatsDocumentRef, {noOfNewNotifications: increment(-1)});
        batch.commit();
      }
      this.handleNotificationByType();
    },

    handleNotificationByType() {
      if (this.selectedNotification.type == 'UPDATE_REQUEST')
        this.handleUpdateRequest();
    },

    async handleUpdateRequest() {
      this.employerUpdateRequests = [];
      var querySnapshot = await getQueryEmployerUpdateRequestsCollection(where('notificationId', '==', this.selectedNotification.notificationId), where('isActioned', '==', false))
          .then((querySnapshot) => {
            querySnapshot.forEach((doc) => {
              let documentData = doc.data();
              this.employerUpdateRequests.push({employerUpdateRequestId: doc.id, ...documentData});
              if (documentData.fieldName == 'mobile') {
                this.selectedTelephoneCode = {
                  countryCode: documentData.countryPhoneCode,
                  number: documentData.countryPhoneCodeNumber
                };
              }
            });
          })
          .catch((error) => {
            console.log("Error getting documents: ", error);
          });
      if (this.employerUpdateRequests.length == 0) {
        //Present the already actioned modal
        this.alreadyActionedDialog = true;
      } else {
        //Present the update reuest modal
        this.updateRequestDialog = true;
      }
    },

    async acceptEmployerUpdateRequest(employerUpdateRequest) {
      if (this.$refs.form.validate()) {
        var db = getFirestore();
        var batch = writeBatch(db);
        //Update the employerRequest
        let notificationDocumentRef = employerUpdateRequestsDocRef(employerUpdateRequest.employerUpdateRequestId);
        batch.update(notificationDocumentRef, {isActioned: true, isApproved: true});
        let businessCardDocumentRef = businessCardsDocRef(this.selectedNotification.businessCardId);
        //Update te user's business card
        if (['firstName', 'lastName', 'jobTitle', 'email', 'mobile'].includes(employerUpdateRequest.fieldName)) {
          await updateBusinessCardsDoc(this.selectedNotification.businessCardId, {[employerUpdateRequest.fieldName]: employerUpdateRequest.newValue});
        }
        if (employerUpdateRequest.fieldName == 'mobile') {
          await updateBusinessCardsDoc(businessCardDocumentRef.id, {
            [employerUpdateRequest.fieldName]: employerUpdateRequest.newValue,
            countryPhoneCode: this.selectedTelephoneCode.countryCode,
            countryPhoneCodeNumber: this.selectedTelephoneCode.number
          });
        }
        // Commit the batch
        batch.commit().then(() => {
          this.employerUpdateRequests.forEach((element, index) => {
            if (element.employerUpdateRequestId == employerUpdateRequest.employerUpdateRequestId)
              this.employerUpdateRequests.splice(index, 1);
          });
          if (this.employerUpdateRequests.length == 0)
            this.updateRequestDialog = false;
        });
      }
    },
    async declineEmployerUpdateRequest(employerUpdateRequest) {
      await updateEmployerUpdateRequestsDoc(employerUpdateRequest.employerUpdateRequestId, {isActioned: true, isApproved: false});
      this.employerUpdateRequests.forEach((element, index) => {
        if (element.employerUpdateRequestId == employerUpdateRequest.employerUpdateRequestId)
          this.employerUpdateRequests.splice(index, 1);
      });
      if (this.employerUpdateRequests.length == 0)
        this.updateRequestDialog = false;
    },

    getReadableFieldName(fieldName) {
      if (fieldName == "firstName")
        return "First Name";
      else if (fieldName == "lastName")
        return "Last Name";
      else if (fieldName == "email")
        return "Email";
      else if (fieldName == "mobile")
        return "Mobile";
      else if (fieldName == "jobTitle")
        return "Job Title";
      else
        return '';
    },

    filterTelephoneCodeAutocompleteBox(item, queryText, itemText) {
      if (item.name.toUpperCase().includes(queryText.toUpperCase()) || itemText.toUpperCase().includes(queryText.toUpperCase()))
        return true;
      else
        return false;
    },
  },
}
</script>